import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ANALYSIS_WRONG_STEP_CODE,
  API_VIRTUAL_RULE_KEYS,
  ApiError,
  ApiErrorBody,
  ValidationErrorClassification,
} from '@core/models/api-error.model';

const VIRTUAL_RULE_KEY_SET = new Set<string>([...API_VIRTUAL_RULE_KEYS, 'landholding']);

/** Indexed landholding area on Etapa 4 writes (entries `area_utilizada_ha`). */
const OPERATION_LANDHOLDING_AREA_PATH =
  /^landholdings\.\d+\.(area_explorada_ha|area_utilizada_ha|operation_landholding_id)$/;

/** Indexed crop fields on Etapa 4 writes (`hectares` on entries, `area_utilizada_ha` on selection). */
const OPERATION_LANDHOLDING_CROP_PATH =
  /^landholdings\.\d+\.crops\.\d+\.(cultura|safra_tipo|safra_ano|area_utilizada_ha|hectares)$/;

/** Root aggregated crops on `POST .../operation/entries`. */
const OPERATION_AGGREGATED_CROP_PATH = /^crops(\.\d+\.cultura)?$/;

/** Collection root keys on `POST .../operation/entries` with `operations[]`. */
const OPERATION_COLLECTION_ROOT_PATH = /^(operations|operation_type|entity_profile)$/;

/**
 * Anything under one `operations[]` item. Which card, vínculo or crop line it lands on is
 * the projection's call (`projectOperationCollectionValidationErrors`), not this one's, so
 * the two never disagree on what counts as a field.
 */
const OPERATION_COLLECTION_ITEM_PATH = /^operations\.\d+\.[a-z_.\d]+$/;

@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  /** Field keys the Etapa Documentos consumer treats as upload/file selectors. */
  static readonly VCR_DOCUMENT_FIELD_KEYS = new Set(['file', 'files', 'documento']);

  /** Write-field keys of Etapa 4 block 1 (`POST .../operation/entries`). */
  static readonly VCR_OPERATION_SAVE_FIELD_KEYS = new Set([
    'operation_type',
    'valor_credito_brl',
    'garantias_notes',
    'operation_notes',
    'entity_profile',
    'crops',
  ]);

  /** Write-field keys on collection `POST .../operation/entries` (`operations[]` body). */
  static readonly VCR_OPERATION_COLLECTION_SAVE_FIELD_KEYS = new Set([
    'operation_type',
    'entity_profile',
    'operations',
  ]);

  /** Write-field keys of Etapa 5 (anuências / autorização). */
  static readonly VCR_CONSENT_FIELD_KEYS = new Set([
    'whatsapp_number',
    'signer_id',
    'tipo',
    'consent',
    'justification',
    'file',
  ]);

  /** Write-field keys of Etapa 3 (inventário de imóveis rurais). */
  static readonly VCR_RURAL_PROPERTY_FIELD_KEYS = new Set([
    'tenure_regime',
    'descricao',
    'uf',
    'municipio',
    'area_total_ha',
    'area_explorada_ha',
    'pct_receita_tomador',
    'pct_receita_contraparte',
    'valor_declarado_brl',
    'nirf',
    'codigo_incra',
    'source',
    'source_extraction_id',
    'source_qsa_id',
    'official_property_id',
    'official_candidate_key',
    'declared_candidate_keys',
    'candidate_key',
  ]);

  fromHttpError(error: HttpErrorResponse): ApiError {
    const body = this.asErrorBody(error.error);

    return {
      status: error.status,
      message: typeof body?.message === 'string' ? body.message : null,
      errors: body?.errors ?? {},
      providerError: typeof body?.error === 'string' ? body.error : null,
      retryAfter: this.parseRetryAfter(this.readHeader(error.headers, 'Retry-After')),
    };
  }

  /**
   * Splits a `422` into field errors, virtual rules and unknown keys. Returns `null`
   * when the error is not a `422` (or has no usable body).
   */
  parseValidationError(
    error: unknown,
    knownFieldKeys: ReadonlySet<string> = ApiErrorService.VCR_DOCUMENT_FIELD_KEYS,
  ): ValidationErrorClassification | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 422) {
      return null;
    }

    const body = this.asErrorBody(error.error);
    const fieldErrors: Record<string, readonly string[]> = {};
    const virtualRuleErrors: Record<string, readonly string[]> = {};
    const unknownErrors: Record<string, readonly string[]> = {};
    const allMessages: string[] = [];

    const errors = body?.errors ?? {};
    for (const [key, messages] of Object.entries(errors)) {
      const list = this.normalizeMessages(messages);
      allMessages.push(...list);

      if (this.isFieldErrorKey(key, knownFieldKeys)) {
        fieldErrors[key] = list;
      } else if (VIRTUAL_RULE_KEY_SET.has(key)) {
        virtualRuleErrors[key] = list;
      } else {
        unknownErrors[key] = list;
      }
    }

    const message = typeof body?.message === 'string' ? body.message : null;
    if (allMessages.length === 0 && message) {
      allMessages.push(message);
    }

    const code = typeof body?.code === 'string' ? body.code : null;
    const currentStep =
      body?.current_step === null
        ? null
        : typeof body?.current_step === 'string'
          ? body.current_step
          : null;

    return {
      message,
      fieldErrors,
      virtualRuleErrors,
      unknownErrors,
      allMessages,
      code,
      currentStep,
    };
  }

  /** Field-level keys: known scalars, indexed uploads, or operation selection paths. */
  isFieldErrorKey(key: string, knownFieldKeys: ReadonlySet<string>): boolean {
    return (
      knownFieldKeys.has(key) ||
      /^files\.\d+$/.test(key) ||
      OPERATION_LANDHOLDING_AREA_PATH.test(key) ||
      OPERATION_LANDHOLDING_CROP_PATH.test(key) ||
      OPERATION_AGGREGATED_CROP_PATH.test(key) ||
      (knownFieldKeys.has('operations') && this.isOperationCollectionFieldErrorKey(key))
    );
  }

  /**
   * Collection `POST .../operation/entries` paths (`operations[]` body). Only
   * consulted when `knownFieldKeys` includes `operations` (collection persist).
   */
  isOperationCollectionFieldErrorKey(key: string): boolean {
    return OPERATION_COLLECTION_ROOT_PATH.test(key) || OPERATION_COLLECTION_ITEM_PATH.test(key);
  }

  flattenMessages(map: Readonly<Record<string, readonly string[]>>): readonly string[] {
    return Object.values(map).flat();
  }

  isThrottleError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 429;
  }

  isStepMismatchError(classification: ValidationErrorClassification): boolean {
    if (classification.code === ANALYSIS_WRONG_STEP_CODE) {
      return true;
    }

    return (classification.virtualRuleErrors['step']?.length ?? 0) > 0;
  }

  hasVisibleValidationMessages(classification: ValidationErrorClassification): boolean {
    return this.topAlertMessages(classification).length > 0;
  }

  /**
   * User-facing copy for list/query `422`s: surfaces field keys such as
   * `per_page`, `search`, `status.0`, `step.0` and date-window params from
   * `errors`, then falls back to the root message or a generic key.
   */
  listQueryErrorMessage(error: unknown, fallback: string): string {
    const classified = this.parseValidationError(error);
    if (classified) {
      const alerts = this.topAlertMessages(classified);
      if (alerts.length > 0) {
        return alerts.join(' ');
      }
      if (classified.message) {
        return classified.message;
      }
      return fallback;
    }

    if (error instanceof HttpErrorResponse) {
      const body = this.asErrorBody(error.error);
      if (body?.message) {
        return body.message;
      }
    }

    return fallback;
  }

  /**
   * User-facing copy for a write (`POST`/`PATCH`). Same as `listQueryErrorMessage`
   * for a `4xx` the backend validated, but a `5xx` body never reaches the screen:
   * it carries stack-shaped text (`SQLSTATE[...]`, English framework strings) the
   * analyst cannot act on, so the caller's own copy is used and the backend text is
   * logged instead.
   */
  writeErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse && error.status >= 500) {
      const body = this.asErrorBody(error.error);
      if (body?.message) {
        console.error('[api] server error on write:', body.message);
      }
      return fallback;
    }

    return this.listQueryErrorMessage(error, fallback);
  }

  topAlertMessages(classification: ValidationErrorClassification): readonly string[] {
    const visibleVirtualRules = { ...classification.virtualRuleErrors };
    delete visibleVirtualRules['step'];

    const fromRules = this.flattenMessages(visibleVirtualRules);
    const fromUnknown = this.flattenMessages(classification.unknownErrors);

    if (fromRules.length > 0 || fromUnknown.length > 0) {
      return [...fromRules, ...fromUnknown];
    }

    if (this.isStepMismatchError(classification)) {
      return [];
    }

    if (classification.message) {
      return [classification.message];
    }

    return classification.allMessages;
  }

  private readHeader(headers: HttpErrorResponse['headers'], name: string): string | null {
    if (!headers) {
      return null;
    }

    if (typeof headers.get === 'function') {
      return headers.get(name);
    }

    const record = headers as unknown as Record<string, string | string[] | undefined>;
    const value = record[name] ?? record[name.toLowerCase()];

    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return typeof value === 'string' ? value : null;
  }

  private normalizeMessages(messages: unknown): readonly string[] {
    if (!Array.isArray(messages)) {
      return [];
    }

    return messages.filter((item): item is string => typeof item === 'string');
  }

  private parseRetryAfter(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const seconds = Number(value);
    return Number.isFinite(seconds) ? seconds : null;
  }

  private asErrorBody(value: unknown): ApiErrorBody | null {
    if (value === null || typeof value !== 'object') {
      return null;
    }

    return value as ApiErrorBody;
  }
}
